import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './StaffManagement.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in';
const STAFF_API = `${BASE_URL}/api/staff-management`;

const ROLE_OPTIONS = [
  { value: 'manager', label: 'Manager', note: 'Shop handle karega, lekin staff/settings full control nahi.' },
  { value: 'kitchen', label: 'Kitchen Staff', note: 'Sirf KOT, kitchen queue aur ready status.' },
  { value: 'counter', label: 'Counter Staff', note: 'Counter order, accept, send kitchen aur KOT print.' },
  { value: 'billing', label: 'Billing Staff', note: 'Invoice, payment, bill print aur complete order.' },
  { value: 'delivery_manager', label: 'Delivery Manager', note: 'Delivery assign/reassign aur delivery records.' },
];

const GROUPS = [
  { title: 'Orders', icon: '📋', items: [
    ['ordersView', 'Orders View', 'Orders list dekhna'],
    ['orderAccept', 'Accept Order', 'New order accept karna'],
    ['sendToKitchen', 'Send to Kitchen', 'Kitchen me order bhejna'],
    ['cancelOrder', 'Cancel Order', 'Order cancel karna'],
  ]},
  { title: 'KOT / Kitchen', icon: '👨‍🍳', items: [
    ['kotView', 'KOT View', 'KOT dekhna'],
    ['kitchenQueue', 'Kitchen Queue', 'Kitchen queue access'],
    ['markPreparing', 'Mark Preparing', 'Preparing/Kitchen status'],
    ['markReady', 'Mark Ready', 'Ready status'],
    ['printKot', 'Print KOT', 'KOT print'],
  ]},
  { title: 'Counter / Billing', icon: '🧾', items: [
    ['counterOrder', 'Counter Order', 'Walk-in order create'],
    ['invoiceView', 'Invoice View', 'Invoice dekhna'],
    ['invoiceEdit', 'Invoice Edit', 'Discount/GST/charges edit'],
    ['billPrint', 'Bill Print', 'Bill print'],
    ['paymentUpdate', 'Payment Update', 'Payment status/method'],
    ['completeOrder', 'Complete Order', 'Order complete'],
  ]},
  { title: 'Delivery', icon: '🛵', items: [
    ['deliveryDispatch', 'Delivery Dispatch', 'Ready delivery panel'],
    ['assignRider', 'Assign Rider', 'Rider assign'],
    ['reassignRider', 'Reassign Rider', 'Rider change'],
    ['deliveryRecords', 'Delivery Records', 'Trip records'],
    ['riderManage', 'Rider Manage', 'Rider reset PIN/delete'],
  ]},
  { title: 'Reports / Settings', icon: '⚙️', items: [
    ['recordsView', 'Records View', 'Sales records'],
    ['reportsView', 'Reports View', 'Reports dekhna'],
    ['expenseManage', 'Expense Manage', 'Expense add/export'],
    ['productManage', 'Product/Menu Manage', 'Product menu control'],
    ['qrManage', 'QR Manage', 'QR active/inactive/generate'],
    ['settingsManage', 'Settings Manage', 'Shop/order settings'],
    ['staffManagement', 'Staff Management', 'Staff add/edit/delete'],
  ]},
];

const allPermissionKeys = GROUPS.flatMap((g) => g.items.map((i) => i[0]));
const emptyPermissions = allPermissionKeys.reduce((acc, key) => ({ ...acc, [key]: false }), {});

const PRESETS = {
  manager: {
    ordersView: true, orderAccept: true, sendToKitchen: true, cancelOrder: true,
    kotView: true, kitchenQueue: true, markPreparing: true, markReady: true, printKot: true,
    counterOrder: true, invoiceView: true, invoiceEdit: true, billPrint: true, paymentUpdate: true, completeOrder: true,
    deliveryDispatch: true, assignRider: true, reassignRider: true, deliveryRecords: true, riderManage: false,
    recordsView: true, reportsView: true, expenseManage: false, productManage: false, qrManage: false, settingsManage: false, staffManagement: false,
  },
  kitchen: {
    ...emptyPermissions,
    kotView: true, kitchenQueue: true, markPreparing: true, markReady: true, printKot: true,
  },
  counter: {
    ...emptyPermissions,
    ordersView: true, orderAccept: true, sendToKitchen: true, kotView: true, printKot: true, counterOrder: true,
  },
  billing: {
    ...emptyPermissions,
    ordersView: true, invoiceView: true, invoiceEdit: true, billPrint: true, paymentUpdate: true, completeOrder: true,
  },
  delivery_manager: {
    ...emptyPermissions,
    deliveryDispatch: true, assignRider: true, reassignRider: true, deliveryRecords: true, riderManage: true,
  },
};

const initialForm = {
  staffName: '',
  mobile: '',
  pin: '',
  role: 'kitchen',
  status: 'active',
  permissions: { ...emptyPermissions, ...PRESETS.kitchen },
};

const getRoleLabel = (role) => ROLE_OPTIONS.find((item) => item.value === role)?.label || 'Staff';
const cleanMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, 10);
const cleanPin = (value) => String(value || '').replace(/\D/g, '').slice(0, 6);
const permissionCount = (permissions = {}) => Object.values(permissions).filter(Boolean).length;
const getStoredToken = () => localStorage.getItem('token') || sessionStorage.getItem('token') || '';

const normalizeStaff = (staff = {}) => ({
  id: staff.id || staff.staff_id,
  staffName: staff.staffName || staff.staff_name || staff.name || 'Staff',
  mobile: staff.mobile || staff.staff_mobile || '',
  role: staff.role || 'kitchen',
  status: staff.status || 'active',
  permissions: { ...emptyPermissions, ...(staff.permissions || {}) },
  createdAt: staff.createdAt || staff.created_at || '',
  updatedAt: staff.updatedAt || staff.updated_at || '',
});

const StaffManagement = ({ vendorId, vendorType = 'fastfood', getToken, showMessage }) => {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = typeof getToken === 'function' ? getToken() : getStoredToken();

  const notify = (text, type = 'success') => {
    if (typeof showMessage === 'function') showMessage(text, type);
    else if (type === 'error') window.alert(text);
  };

  const staffLoginLink = useMemo(() => {
    const origin = window.location?.origin || 'https://vakra.co.in';
    return `${origin}/staff-login?vendor=${vendorId || ''}&vendorType=${encodeURIComponent(vendorType || 'fastfood')}`;
  }, [vendorId, vendorType]);

  const selectedStaff = useMemo(
    () => staffList.find((staff) => String(staff.id) === String(selectedStaffId)) || null,
    [staffList, selectedStaffId]
  );

  const visibleStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staffList.filter((staff) => {
      const matchesSearch = !q ||
        String(staff.staffName).toLowerCase().includes(q) ||
        String(staff.mobile).includes(q) ||
        String(staff.role).toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || staff.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || staff.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [staffList, search, roleFilter, statusFilter]);

  const fetchStaff = async () => {
    if (!token) {
      notify('Login token missing hai.', 'error');
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(STAFF_API, {
        params: { vendorType },
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = Array.isArray(res.data?.staff) ? res.data.staff : Array.isArray(res.data) ? res.data : [];
      const list = raw.map(normalizeStaff);
      setStaffList(list);
      if (!selectedStaffId && list.length) setSelectedStaffId(list[0].id);
    } catch (err) {
      notify(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Staff list load nahi hui.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();

  }, [vendorType]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingStaffId(null);
  };

  const applyRolePreset = (role) => {
    setForm((prev) => ({
      ...prev,
      role,
      permissions: { ...emptyPermissions, ...(PRESETS[role] || {}) },
    }));
  };

  const startEdit = (staff) => {
    setEditingStaffId(staff.id);
    setSelectedStaffId(staff.id);
    setForm({
      staffName: staff.staffName || '',
      mobile: cleanMobile(staff.mobile),
      pin: '',
      role: staff.role || 'kitchen',
      status: staff.status || 'active',
      permissions: { ...emptyPermissions, ...(staff.permissions || {}) },
    });
  };

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  const setAllPermissions = (checked) => {
    const next = { ...emptyPermissions };
    Object.keys(next).forEach((key) => { next[key] = checked; });
    if (checked) next.staffManagement = false;
    setForm((prev) => ({ ...prev, permissions: next }));
  };

  const saveStaff = async (event) => {
    event.preventDefault();
    const staffName = form.staffName.trim();
    const mobile = cleanMobile(form.mobile);
    const pin = cleanPin(form.pin);

    if (!staffName) return notify('Staff name required hai.', 'error');
    if (mobile.length !== 10) return notify('Valid 10 digit mobile number required hai.', 'error');
    if (!editingStaffId && pin.length < 4) return notify('New staff ke liye 4-6 digit PIN required hai.', 'error');
    if (editingStaffId && pin && pin.length < 4) return notify('PIN change karna hai to minimum 4 digit enter karein.', 'error');

    try {
      setSaving(true);
      const payload = {
        staffName,
        mobile,
        pin: pin || undefined,
        role: form.role,
        status: form.status,
        vendorType,
        permissions: form.permissions,
      };

      if (editingStaffId) {
        await axios.put(`${STAFF_API}/${editingStaffId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        notify('Staff update ho gaya.');
      } else {
        await axios.post(STAFF_API, payload, { headers: { Authorization: `Bearer ${token}` } });
        notify('Staff add ho gaya.');
      }

      resetForm();
      await fetchStaff();
    } catch (err) {
      notify(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Staff save nahi hua.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (staff, status) => {
    if (!staff?.id) return;
    try {
      setSaving(true);
      await axios.patch(`${STAFF_API}/${staff.id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      notify(`Staff ${status} ho gaya.`);
      await fetchStaff();
    } catch (err) {
      notify(err?.response?.data?.message || err.message || 'Status update nahi hua.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetPin = async (staff) => {
    const pin = cleanPin(window.prompt(`${staff.staffName} ka new PIN enter karein:`) || '');
    if (!pin) return;
    if (pin.length < 4) return notify('PIN minimum 4 digit hona chahiye.', 'error');
    try {
      setSaving(true);
      await axios.patch(`${STAFF_API}/${staff.id}/reset-pin`, { pin }, { headers: { Authorization: `Bearer ${token}` } });
      notify('PIN reset ho gaya.');
    } catch (err) {
      notify(err?.response?.data?.message || err.message || 'PIN reset nahi hua.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteStaff = async (staff) => {
    if (!window.confirm(`${staff.staffName} ko delete karna hai?`)) return;
    try {
      setSaving(true);
      await axios.delete(`${STAFF_API}/${staff.id}`, { headers: { Authorization: `Bearer ${token}` } });
      notify('Staff delete ho gaya.');
      resetForm();
      await fetchStaff();
    } catch (err) {
      notify(err?.response?.data?.message || err.message || 'Staff delete nahi hua.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copyLoginLink = async () => {
    try {
      await navigator.clipboard.writeText(staffLoginLink);
      notify('Staff login link copied.');
    } catch {
      notify('Staff login link copy nahi hua.', 'error');
    }
  };

  const shareWhatsApp = () => {
    const text = `Staff Login Link%0A${encodeURIComponent(staffLoginLink)}%0A%0AMobile number aur PIN se login karein.`;
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="staff-mgmt-page">
      <div className="staff-mgmt-header">
        <div>
          <p className="staff-mgmt-eyebrow">Staff Access Control</p>
          <h2>Staff Management</h2>
          <p>Owner yaha se staff add karega aur page/action permission select karega.</p>
        </div>
        <div className="staff-mgmt-header-actions">
          <button type="button" onClick={copyLoginLink}>Copy Staff Login</button>
          <button type="button" className="whatsapp" onClick={shareWhatsApp}>WhatsApp</button>
          <button type="button" onClick={fetchStaff} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
        </div>
      </div>

      <div className="staff-mgmt-link-box">
        <span>Staff Login:</span>
        <code>{staffLoginLink}</code>
      </div>

      <div className="staff-mgmt-layout">
        <aside className="staff-mgmt-list-card">
          <div className="staff-mgmt-list-head">
            <h3>Staff List</h3>
            <button type="button" onClick={resetForm}>+ New</button>
          </div>

          <div className="staff-mgmt-filters">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff" />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              {ROLE_OPTIONS.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>

          {visibleStaff.length ? (
            <div className="staff-mgmt-list">
              {visibleStaff.map((staff) => (
                <button
                  key={staff.id}
                  type="button"
                  className={`staff-mgmt-list-item ${String(selectedStaffId) === String(staff.id) ? 'selected' : ''} ${staff.status === 'inactive' ? 'inactive' : ''}`}
                  onClick={() => setSelectedStaffId(staff.id)}
                >
                  <span className="staff-avatar">{staff.staffName.slice(0, 1).toUpperCase()}</span>
                  <span className="staff-info">
                    <strong>{staff.staffName}</strong>
                    <small>{staff.mobile || '-'}</small>
                    <em>{getRoleLabel(staff.role)}</em>
                  </span>
                  <span className={`staff-status ${staff.status}`}>{staff.status}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="staff-mgmt-empty">Staff nahi mila.</div>
          )}
        </aside>

        <main className="staff-mgmt-main-card">
          <form onSubmit={saveStaff}>
            <div className="staff-mgmt-form-head">
              <div>
                <h3>{editingStaffId ? 'Edit Staff' : 'Add Staff'}</h3>
                <p>Role select karte hi default permission auto select hogi.</p>
              </div>
              <div className="staff-mgmt-form-actions">
                <button type="button" onClick={() => setAllPermissions(true)}>Select All</button>
                <button type="button" onClick={() => setAllPermissions(false)}>Clear All</button>
                <button type="button" onClick={resetForm}>Reset</button>
              </div>
            </div>

            <div className="staff-mgmt-basic-grid">
              <label>Staff Name *<input value={form.staffName} onChange={(e) => setForm((p) => ({ ...p, staffName: e.target.value }))} placeholder="e.g. Ramesh" /></label>
              <label>Mobile Number *<input value={form.mobile} onChange={(e) => setForm((p) => ({ ...p, mobile: cleanMobile(e.target.value) }))} placeholder="10 digit mobile" inputMode="numeric" /></label>
              <label>{editingStaffId ? 'New PIN optional' : 'PIN *'}<input value={form.pin} onChange={(e) => setForm((p) => ({ ...p, pin: cleanPin(e.target.value) }))} placeholder="4-6 digit PIN" inputMode="numeric" /></label>
              <label>Role *<select value={form.role} onChange={(e) => applyRolePreset(e.target.value)}>{ROLE_OPTIONS.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}</select></label>
              <label>Status<select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
            </div>

            <div className="staff-role-help"><strong>{getRoleLabel(form.role)}</strong><span>{ROLE_OPTIONS.find((r) => r.value === form.role)?.note}</span></div>
            <div className="staff-permission-summary"><span>{permissionCount(form.permissions)} permission selected</span><small>Staff Management normally OFF rakho.</small></div>

            <div className="staff-permission-groups">
              {GROUPS.map((group) => (
                <div className="staff-permission-group" key={group.title}>
                  <div className="staff-permission-group-title"><span>{group.icon}</span><strong>{group.title}</strong></div>
                  <div className="staff-permission-grid">
                    {group.items.map(([key, label, help]) => (
                      <label key={key} className={`staff-permission-item ${form.permissions[key] ? 'checked' : ''}`}>
                        <input type="checkbox" checked={Boolean(form.permissions[key])} onChange={() => togglePermission(key)} />
                        <span><strong>{label}</strong><small>{help}</small></span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="staff-mgmt-submit-row">
              <button type="submit" className="primary" disabled={saving}>{saving ? 'Saving...' : editingStaffId ? 'Update Staff' : 'Add Staff'}</button>
              {editingStaffId ? <button type="button" onClick={resetForm}>Cancel Edit</button> : null}
            </div>
          </form>
        </main>

        <aside className="staff-mgmt-detail-card">
          <h3>Selected Staff</h3>
          {selectedStaff ? (
            <>
              <div className="staff-detail-profile"><span className="staff-detail-avatar">{selectedStaff.staffName.slice(0, 1).toUpperCase()}</span><div><strong>{selectedStaff.staffName}</strong><small>{selectedStaff.mobile || '-'}</small><em>{getRoleLabel(selectedStaff.role)}</em></div></div>
              <div className="staff-detail-stats"><div><strong>{permissionCount(selectedStaff.permissions)}</strong><span>Permissions</span></div><div><strong>{selectedStaff.status}</strong><span>Status</span></div></div>
              <div className="staff-detail-actions">
                <button type="button" onClick={() => startEdit(selectedStaff)}>Edit Access</button>
                <button type="button" onClick={() => resetPin(selectedStaff)}>Reset PIN</button>
                {selectedStaff.status === 'inactive'
                  ? <button type="button" onClick={() => updateStatus(selectedStaff, 'active')}>Make Active</button>
                  : <button type="button" className="warning" onClick={() => updateStatus(selectedStaff, 'inactive')}>Make Inactive</button>}
                <button type="button" className="danger" onClick={() => deleteStaff(selectedStaff)}>Delete</button>
              </div>
              <div className="staff-detail-permissions">
                <h4>Allowed Permissions</h4>
                {Object.entries(selectedStaff.permissions || {}).filter(([, value]) => value).map(([key]) => <span key={key}>{key}</span>)}
                {!permissionCount(selectedStaff.permissions) ? <p>No permission selected.</p> : null}
              </div>
            </>
          ) : <p className="staff-mgmt-empty">Staff select karein.</p>}
        </aside>
      </div>
    </section>
  );
};

export default StaffManagement;
